/**
 * TrackSummaryCard — what a track holds, read in place wherever the track is
 * named (the item page's Prioritization tab first). It opens from the track's
 * name through `<ObjectRef kind="track" … summary={…} />` (ds/Popover).
 *
 * It answers, in the order the work happens: who owns the track, whether each
 * part it is set up from is ready (participants, item pool, criteria, value
 * scales, values — the server's own readiness check, `evaluate_track_readiness`,
 * canon glossary "Track Readiness Conditions"), how many sessions have ranked,
 * what result is published for the track (glossary "Published Track Result":
 * published by the person who holds the ball), and where this item sits in it.
 *
 * The page hands in what it already holds (names, the pool, the criteria); the
 * card reads the three server facts itself, and only once it is open.
 */
import { PopoverRows } from '@/components/ds';
import { trackSummaryRows, type TrackSummaryBasics } from './trackSummaryRows';
import { useTrackSessionCounts, useTrackSummaryServerFacts } from './useTrackSummaryFacts';

export type { TrackSummaryBasics } from './trackSummaryRows';

export interface TrackSummaryCardProps {
  basics: TrackSummaryBasics;
  /** The item whose page this is: the card says where it sits in the published result. */
  itemId?: string;
}

export function TrackSummaryCard({ basics, itemId }: TrackSummaryCardProps) {
  const { trackId } = basics;
  // Each answer fills its own rows as it arrives.
  const server = useTrackSummaryServerFacts(trackId, itemId);
  const sessions = useTrackSessionCounts(trackId);

  return (
    <PopoverRows
      heading={basics.cycleName ? `Track · in ${basics.cycleName}` : 'Standalone track · in no cycle'}
      rows={trackSummaryRows(basics, sessions, server, itemId)}
      link={{ label: 'Open the track', href: basics.href }}
    />
  );
}
